import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { useEffect, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import InputBase from '@mui/material/InputBase';
import Router from "next/router";
import dynamic from 'next/dynamic';
import actions from '@components/Redux/Global/actions';
import { useDispatch, useSelector } from 'react-redux';
import useCartHandler from '@components/_hooks/Cart/useCartHandler';
import useCartDispatch from '@components/_hooks/Cart/useCartDispatch';
import useLiveProductSearch from '../hooks/useLiveProductSearch';
import HeaderSearchDropdown from '../HeaderSearchDropdown';
import exactProductLink from '../utils/exactProductLink';
import useExtraDialog from '@components/_hooks/Cart/useExtraDialog';
import { SEARCH_SECTION_ID } from '@components/config/constants';
import { extrasDialogData } from '@components/config/productExtras';

const ProductExtrasDialog = dynamic(() => import('@components/Components/ProductComponent/ProductExtrasDialog'), { ssr: false });

const StyledBox = dynamic(() => import("@components/Custom/Box/StyledBox"));

const SearchIconWrapper = styled('button')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  all: 'unset',
  top: '25%',
  cursor: 'pointer',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '96%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(0.5em + ${theme.spacing(1.35)})`,
    paddingRight: `calc(0.5em + ${theme.spacing(0.55)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '100%',
    },
  },
}));

const HeaderSearch = () => {
  const dispatch = useDispatch();
  const { addToCart } = useCartHandler();
  const { SET_TOP_CART_MODAL } = useCartDispatch();
  const { TopCartModal } = useSelector((state) => state.modalsReducer);

  const [placeholder, setPlaceholder] = useState("Search");
  const [search, setSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { open: extrasOpen, handleToggle: toggleExtras, extraProductData, setExtraProductData } = useExtraDialog();
  const inputRef = useRef(null);

  const { products, total, loading, loadingMore, moreFailed, hasMore, loadMore, error } = useLiveProductSearch({
    query: search,
    open: dropdownOpen && !TopCartModal?.open,
  });

  // Ensure search dropdown closes whenever TopCartModal opens
  useEffect(() => {
    if (TopCartModal?.open) {
      setDropdownOpen(false);
    }
  }, [TopCartModal?.open]);

  useEffect(() => {
    const handleRouteChange = (url) => {
      setDropdownOpen(false);
      if (url.startsWith('/search')) {
        const storedTerm = typeof window !== 'undefined' ? sessionStorage.getItem('search_term') || '' : '';
        setSearch(storedTerm);
      } else {
        setSearch('');
      }
    };

    Router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, []);

  // The product is a real link (see SearchResultRow), which does the navigating: choosing it only closes the dropdown.
  const handleSelectProduct = () => {
    setDropdownOpen(false);
  };

  const handleAddToQuote = async (product) => {
    // Immediately close search dropdown and blur input to prevent modal collision
    setDropdownOpen(false);
    if (inputRef.current) {
      inputRef.current.blur();
    }

    const productID = product?.product_id || product?.id;
    const slug = product?.slug || product?.product_slug;
    if (productID && slug) {
      await addToCart(productID, slug, 1, 'search_header', SEARCH_SECTION_ID);
    }
  };

  const handleOptionsClick = (product) => {
    setDropdownOpen(false);
    if (inputRef.current) {
      inputRef.current.blur();
    }
    setExtraProductData(extrasDialogData({ product, qty: 1, source: 'search_header', sectionID: SEARCH_SECTION_ID }));
    toggleExtras();
  };

  const handleBrowseAll = () => {
    const searchTerm = search.trim();
    if (!searchTerm) {
      return;
    }

    setDropdownOpen(false);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem("search_term", searchTerm);
    }
    dispatch({
      type: actions.SET_GLOBAL_DATA,
      payload: {
        search_term: searchTerm,
      },
    });

    Router.push('/search');
  };

  const validateSearchForm = async (event) => {
    event.preventDefault();

    // A search for the exact code of a product ("HT12") goes straight to that product, without the search page being seen.
    const productAddress = await exactProductLink(search);
    if (productAddress) {
      setDropdownOpen(false);
      Router.push(productAddress);
      return;
    }

    handleBrowseAll();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setDropdownOpen(false);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
      <Box
        sx={{
          marginTop: 'auto',
          marginBottom: 'auto',
          display: 'flex',
          minWidth: '100%',
          maxWidth: '100%',
          position: 'relative',
        }}
        className='ml-4 w-100 mr-1'
      >
        <form onSubmit={validateSearchForm} className='w-100 mr-3'>
          <Box
            className='w-100'
            sx={{
              position: 'relative',
              borderRadius: 15,
              border: 1,
              borderColor: '#ccc',
              minWidth: 'auto',
              maxWidth: '100%',
              ":hover": { backgroundColor: alpha('#ccc', 0.25) },
            }}
          >
            <SearchIconWrapper
              aria-label='search_button'
              name="search_button"
              className="position-absolute"
              sx={{ position: 'absolute', right: 10 }}
            >
              <StyledBox className='p-0'>
                <SearchIcon />
              </StyledBox>
            </SearchIconWrapper>
            <StyledInputBase
              placeholder={placeholder}
              inputProps={{ 'aria-label': 'search', name: 'search' }}
              type="text"
              inputRef={inputRef}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setDropdownOpen(true);
              }}
              onFocus={() => {
                if (TopCartModal?.open) {
                  SET_TOP_CART_MODAL(false);
                }
                if (search.trim() !== '') {
                  setDropdownOpen(true);
                }
              }}
              onKeyDown={handleKeyDown}
              name="term"
              id="term"
              autoComplete="off"
            />
          </Box>
        </form>

        <HeaderSearchDropdown
          open={dropdownOpen && !TopCartModal?.open}
          query={search}
          loading={loading}
          loadingMore={loadingMore}
          moreFailed={moreFailed}
          hasMore={hasMore}
          onLoadMore={loadMore}
          products={products}
          total={total}
          error={error}
          onSelectProduct={handleSelectProduct}
          onBrowseAll={handleBrowseAll}
          onAddToQuote={handleAddToQuote}
          onOptionsClick={handleOptionsClick}
          isMobile={false}
        />

        {extrasOpen && extraProductData?.product?.product_id && (
          <ProductExtrasDialog
            extrasData={extraProductData}
            open={extrasOpen}
            toggle={toggleExtras}
          />
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default HeaderSearch;